import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Package, RefreshCw, AlertCircle, Truck, ExternalLink, MapPin, CreditCard, Tag, Clock, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface OrderHistoryProps {
  userEmail: string | null;
}

interface TrackingInfo {
  number: string;
  url: string | null;
  company: string | null;
}

interface OrderItem {
  title: string;
  variantTitle: string | null;
  quantity: number;
  sku: string | null;
  originalPrice: string | null;
  discountedPrice: string | null;
  imageUrl: string | null;
}

interface Order {
  id: string;
  orderNumber: string;
  createdAt: string;
  processedAt: string | null;
  financialStatus: string;
  fulfillmentStatus: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  note: string | null;
  email: string | null;
  phone: string | null;
  totalPrice: string;
  currencyCode: string;
  subtotalPrice: string | null;
  totalShipping: string | null;
  totalTax: string | null;
  totalDiscounts: string | null;
  shippingAddress: string | null;
  shippingMethod: string | null;
  shippingCost: string | null;
  trackingInfo: TrackingInfo[] | null;
  fulfillments: Array<{ status: string | null; createdAt: string | null }> | null;
  items: OrderItem[];
}

const OrderHistory = ({ userEmail }: OrderHistoryProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { t, isHebrew } = useStaticTranslation("orderHistory");

  const translateStatus = (status: string | null | undefined): string => {
    if (!status) return '';
    // Try translated version first
    const translated = t(status, '');
    if (translated) return translated;
    // Hebrew fallback map
    const hebrewMap: Record<string, string> = {
      'PENDING': 'ממתין לתשלום', 'AUTHORIZED': 'מאושר', 'PARTIALLY_PAID': 'שולם חלקית',
      'PAID': 'שולם', 'PARTIALLY_REFUNDED': 'הוחזר חלקית', 'REFUNDED': 'הוחזר',
      'VOIDED': 'בוטל', 'EXPIRED': 'פג תוקף', 'UNFULFILLED': 'ממתין למשלוח',
      'PARTIALLY_FULFILLED': 'נשלח חלקית', 'FULFILLED': 'נשלח', 'RESTOCKED': 'הוחזר למלאי',
      'ON_HOLD': 'בהמתנה', 'SCHEDULED': 'מתוזמן', 'IN_PROGRESS': 'בתהליך',
      'CUSTOMER': 'בקשת לקוח', 'FRAUD': 'חשד להונאה', 'INVENTORY': 'חוסר במלאי',
      'DECLINED': 'תשלום נדחה', 'OTHER': 'סיבה אחרת',
      'SUCCESS': 'הושלם', 'OPEN': 'פתוח', 'CANCELLED': 'בוטל', 'ERROR': 'שגיאה', 'FAILURE': 'נכשל',
    };
    return hebrewMap[status] || status;
  };

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    };
    checkAdmin();
  }, []);

  const handleReconnectShopify = async () => {
    setIsReconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('shopify-oauth-init');
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      console.error('OAuth init failed:', err);
      setError({ type: 'general', message: t("reconnectError", "שגיאה בהפעלת תהליך החיבור מחדש") });
    } finally {
      setIsReconnecting(false);
    }
  };

  const fetchOrders = async () => {
    if (!userEmail) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('get-customer-orders');

      if (invokeError) {
        throw invokeError;
      }

      if (data?.error) {
        if (data.error === 'shopify_auth_failed' || data.error === 'shopify_token_missing') {
          setError({
            type: 'auth',
            message: data.message || t("loadError", "לא ניתן להתחבר לשירות ההזמנות כרגע")
          });
          return;
        }
        throw new Error(data.message || data.error);
      }

      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError({
        type: 'general',
        message: t("loadError", "שגיאה בטעינת היסטוריית ההזמנות")
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [userEmail]);

  const dateLocale = isHebrew ? 'he-IL' : 'en-US';

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString(dateLocale, {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return null; }
  };

  const formatShortDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString(dateLocale, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return null; }
  };

  const hasDiscount = (item: OrderItem) => {
    if (!item.originalPrice || !item.discountedPrice) return false;
    return parseFloat(item.originalPrice) > parseFloat(item.discountedPrice);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!userEmail) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">{t("title", "היסטוריית הזמנות")}</CardTitle>
          <CardDescription className="text-right">
            {t("noEmail", "נא להתחבר כדי לראות את ההזמנות שלך")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {t("noEmail", "נא להתחבר כדי לראות את ההזמנות שלך")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">{t("title", "היסטוריית הזמנות")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={fetchOrders} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t("retry", "נסה שוב")}
              </Button>
              {error.type === 'auth' && isAdmin && (
                <Button onClick={handleReconnectShopify} disabled={isReconnecting} className="gap-2">
                  {isReconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {t("reconnectShopify", "חיבור מחדש לשופיפיי")}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">{t("title", "היסטוריית הזמנות")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("noOrders", "אין הזמנות להצגה")}</p>
            <p className="text-xs text-muted-foreground mt-2">{t("ordersAppearHere", "הזמנות יופיעו כאן לאחר ביצוע רכישה")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-right font-light">{t("title", "היסטוריית הזמנות")}</CardTitle>
          <CardDescription className="text-right">{orders.length} {t("ordersCount", "הזמנות")}</CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchOrders}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="border rounded-lg overflow-hidden">
              {/* Order Header */}
              <div className="bg-muted/30 p-4">
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div className="text-right flex-1 min-w-0">
                    <p className="font-semibold text-lg">{order.orderNumber}</p>
                    {formatShortDate(order.createdAt) && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatShortDate(order.createdAt)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {order.financialStatus && (
                      <Badge variant={order.financialStatus === 'PAID' ? 'default' : 'outline'}>
                        {translateStatus(order.financialStatus)}
                      </Badge>
                    )}
                    {order.fulfillmentStatus && (
                      <Badge variant={order.fulfillmentStatus === 'FULFILLED' ? 'secondary' : 'outline'}>
                        {translateStatus(order.fulfillmentStatus)}
                      </Badge>
                    )}
                    {order.cancelledAt && (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        {t("cancelled", "בוטל")}
                      </Badge>
                    )}
                  </div>
                </div>

                {order.cancelledAt && (
                  <div className="mt-2 p-2 bg-destructive/10 rounded text-sm text-right">
                    <span className="text-destructive">{t("cancelledOn", "בוטל ב-")}{formatShortDate(order.cancelledAt)}</span>
                    {order.cancelReason && (
                      <span className="text-muted-foreground"> - {translateStatus(order.cancelReason)}</span>
                    )}
                  </div>
                )}

                {order.note && (
                  <div className="mt-2 p-2 bg-accent/50 rounded text-sm text-right">
                    <span className="font-medium">{t("note", "הערה: ")}</span>
                    {order.note}
                  </div>
                )}
              </div>
              
              {/* Order Items */}
              <div className="p-4">
                <div className="space-y-3">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title} className="w-16 h-16 object-cover rounded-md flex-shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 text-right">
                        <p className="font-medium truncate">{item.title}</p>
                        {item.variantTitle && item.variantTitle !== 'Default Title' && (
                          <p className="text-sm text-muted-foreground">{item.variantTitle}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-sm text-muted-foreground">{t("quantity", "כמות:")} {item.quantity}</span>
                          {item.sku && (
                            <span className="text-xs text-muted-foreground">{t("sku", 'מק"ט:')} {item.sku}</span>
                          )}
                        </div>
                        {(item.originalPrice || item.discountedPrice) && (
                          <div className="flex items-center gap-2 mt-1">
                            {hasDiscount(item) ? (
                              <>
                                <span className="text-sm font-medium text-primary">
                                  {formatPrice(item.discountedPrice!, order.currencyCode)}
                                </span>
                                <span className="text-xs line-through text-muted-foreground">
                                  {formatPrice(item.originalPrice!, order.currencyCode)}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm">
                                {formatPrice(item.discountedPrice || item.originalPrice!, order.currencyCode)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping & Tracking */}
              {(order.shippingAddress || order.shippingMethod || (order.trackingInfo && order.trackingInfo.length > 0)) && (
                <>
                  <Separator />
                  <div className="p-4 space-y-3">
                    {order.shippingAddress && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="text-right">
                          <span className="font-medium">{t("shippingAddress", "כתובת משלוח: ")}</span>
                          <span className="text-muted-foreground">{order.shippingAddress}</span>
                        </div>
                      </div>
                    )}

                    {order.shippingMethod && (
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="text-right">
                          <span className="font-medium">{t("shippingMethod", "שיטת משלוח: ")}</span>
                          <span className="text-muted-foreground">{order.shippingMethod}</span>
                          {order.shippingCost && parseFloat(order.shippingCost) > 0 && (
                            <span className="text-muted-foreground"> ({formatPrice(order.shippingCost, order.currencyCode)})</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {order.trackingInfo && order.trackingInfo.length > 0 && (
                      <div className="bg-primary/5 rounded-md p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Truck className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">{t("trackShipment", "מעקב משלוח")}</span>
                        </div>
                        {order.trackingInfo.map((tracking, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="text-right">
                              {tracking.company && (
                                <span className="text-muted-foreground">{tracking.company}: </span>
                              )}
                              <span className="font-mono">{tracking.number}</span>
                            </div>
                            {tracking.url && (
                              <a href={tracking.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                                <span>{t("track", "עקוב")}</span>
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <Separator />

              {/* Price Summary */}
              <div className="p-4 bg-muted/20">
                <div className="space-y-1 text-sm">
                  {order.subtotalPrice && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("subtotal", "סכום ביניים")}</span>
                      <span>{formatPrice(order.subtotalPrice, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalShipping && parseFloat(order.totalShipping) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("shipping", "משלוח")}</span>
                      <span>{formatPrice(order.totalShipping, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalTax && parseFloat(order.totalTax) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("vat", 'מע"מ')}</span>
                      <span>{formatPrice(order.totalTax, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalDiscounts && parseFloat(order.totalDiscounts) > 0 && (
                    <div className="flex justify-between text-primary">
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {t("discount", "הנחה")}
                      </span>
                      <span>-{formatPrice(order.totalDiscounts, order.currencyCode)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2">
                    <span>{t("total", 'סה"כ')}</span>
                    <span>{formatPrice(order.totalPrice, order.currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderHistory;
